import {
  Button as MuiButton,
  type ButtonProps as MuiButtonProps,
} from '@mui/material';
import { type FC } from 'react';

import { type BaseComponentProps } from '../../utils/types';
import { Tooltip, type TooltipProps } from '../Tooltip';
import { CircularProgress } from '../animations/CircularProgress';
import { Columns } from '../layout/Columns';

// Do not add "classes", "className", "sx", or "style" to the ButtonProps
// Use the other props to style the button ("variant", "color", "fullWidth", "isPrimary" etc.)
// If you must position the button, wrap it in a div and style this element instead.
export interface ButtonProps
  extends BaseComponentProps,
    Pick<
      MuiButtonProps,
      | 'size'
      | 'variant'
      | 'type'
      | 'color'
      | 'disabled'
      | 'onClick'
      | 'href'
      | 'component'
      | 'fullWidth'
      | 'startIcon'
      | 'endIcon'
      | 'autoFocus'
    > {
  /**
   * Style the button as a primary action. If within a `<ButtonGroup />`,
   * this will pull the button to the right of the group.
   *
   * Note that this is different from the `color="primary"` prop: it only
   * affects the variant and positioning of the button.
   */
  isPrimary?: boolean;
  inProgress?: boolean;
  to?: string;
  tooltip?: TooltipProps['title'];
  children?: string;
  target?: string;
  fullWidth?: boolean;
  whiteSpace?: 'normal' | 'nowrap';
}

export const Button: FC<ButtonProps> = ({
  variant,
  disabled,
  children,
  tooltip,
  color = 'secondary',
  isPrimary = false,
  inProgress = false,
  type = 'button',
  whiteSpace = 'normal',
  ...otherProps
}) => (
  <MuiButton
    disabled={inProgress || disabled}
    type={isPrimary ? 'submit' : type}
    variant={isPrimary ? 'contained' : variant}
    color={color}
    sx={{
      whiteSpace,
    }}
    {...otherProps}
  >
    <Tooltip title={tooltip}>
      <Columns alignItems="center">
        {inProgress && <CircularProgress color={color} size="0.8rem" />}
        {children}
      </Columns>
    </Tooltip>
  </MuiButton>
);
